import { useState } from 'react'
import { format } from 'date-fns'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { BackHeader } from 'components/ui/BackHeader/BackHeader'
import { BannerText, BannerWrap } from 'pages/Operations/Shared/opsShared.styled'
import { formatGhs } from 'pages/FuelManagement/Shared/fuelFormatters'
import { Flex } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { IInventoryStockTakeLineResponse } from 'types/api/inventoryControlApi.interface'
import { IStockTakeDetailProps } from 'types/inventoryControl.interface'
import {
  InventoryStockTakeApprovalRole,
  InventoryStockTakeStatus,
} from 'types/inventoryEnums.interface'
import {
  ADJUSTMENT_REASON_LABEL,
  STOCK_TAKE_STATUS_META,
} from '../Shared/inventoryEnumMaps'
import {
  countProgressLabel,
  deviationBannerText,
  isStockTakeBlindActive,
  isStockTakeEditable,
  signedGhs,
  stockTakeApproverLabel,
  stockTakeVarianceTone,
  STOCK_TAKE_APPROVAL_BANNER,
  STOCK_TAKE_BLIND_CAPTION,
  STOCK_TAKE_DEVIATION_CAPTION,
  STOCK_TAKE_VARIANCE_CAPTION,
} from '../Shared/inventoryFormatters'
import {
  ApproverLine,
  CellValue,
  CountInput,
  DetailMetaLine,
  MaskedValue,
  MicroNote,
  OverLimitTag,
  SheetCaption,
  SheetNoteText,
  VarianceValue,
} from './InventoryStockTakes.styled'

const CountCell = ({
  line,
  masked,
  onCount,
}: {
  line: IInventoryStockTakeLineResponse
  masked: boolean
  onCount: IStockTakeDetailProps['onCount']
}) => {
  const [value, setValue] = useState('')

  const commit = () => {
    const qty = Number(value)
    if (value !== '' && !Number.isNaN(qty) && qty >= 0) {
      onCount(line, qty)
    }
  }

  return (
    <CountInput
      type='number'
      value={value}
      placeholder={masked ? '•••' : line.systemQty != null ? `${line.systemQty}` : '0'}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          ;(e.target as HTMLInputElement).blur()
        }
      }}
      onClick={(e) => e.stopPropagation()}
    />
  )
}

const varianceToneOf = (qty: number | null): 'danger' | 'positive' | 'normal' | 'dim' => {
  if (qty == null) return 'dim'
  if (qty < 0) return 'danger'
  if (qty > 0) return 'positive'
  return 'normal'
}

export const StockTakeDetail = ({
  stockTake,
  onCount,
  onStart,
  onSubmitForApproval,
  onApprove,
  onBack,
  actionLoading,
}: IStockTakeDetailProps) => {
  const lines = stockTake.lines ?? []
  const counted = lines.filter((line) => line.countedQty != null).length
  const variance =
    lines.length === 0
      ? null
      : lines.every((line) => line.varianceGhs == null)
        ? null
        : lines.reduce((sum, line) => sum + (line.varianceGhs ?? 0), 0)
  const sheetValueGhs = lines.reduce(
    (sum, line) => sum + Math.abs((line.systemQty ?? 0) * (line.unitCost ?? 0)),
    0,
  )

  const meta = STOCK_TAKE_STATUS_META[stockTake.status] ?? {
    label: String(stockTake.status),
    tone: 'neutral' as const,
  }
  const editable = isStockTakeEditable(stockTake.status)
  const blindActive = isStockTakeBlindActive(stockTake.blind, stockTake.status)
  const deviationLimit = stockTake.deviationLimitGhs ?? null
  const thresholdsRevealed =
    deviationLimit != null &&
    (stockTake.status === InventoryStockTakeStatus.AwaitingApproval ||
      stockTake.status === InventoryStockTakeStatus.Completed)
  const breachedCount = thresholdsRevealed
    ? lines.filter((line) => line.overLimit === true).length
    : 0
  const supervisorRequired =
    stockTake.status === InventoryStockTakeStatus.AwaitingApproval &&
    (stockTake.approvalRole === InventoryStockTakeApprovalRole.Supervisor || breachedCount > 0)

  const scheduledLabel = stockTake.scheduledDate
    ? format(new Date(stockTake.scheduledDate), 'd MMM')
    : '—'
  const startedLabel = stockTake.startedAt ? format(new Date(stockTake.startedAt), 'd MMM') : null
  const approvedAtLabel = stockTake.approvedAt
    ? format(new Date(stockTake.approvedAt), 'd MMM')
    : null
  const locationLabel = stockTake.location?.name ?? '—'

  const columns: IHonestTableColumn<IInventoryStockTakeLineResponse>[] = [
    {
      id: 'sku',
      header: 'SKU',
      cell: (line) => <CellValue>{line.item?.id ?? line.inventoryItemId}</CellValue>,
    },
    {
      id: 'item',
      header: 'ITEM',
      minWidth: '14rem',
      cell: (line) => line.item?.name ?? `Item ${line.inventoryItemId}`,
    },
    {
      id: 'systemQty',
      header: 'SYSTEM QTY',
      align: 'right',
      cell: (line) =>
        blindActive && line.systemQty == null ? (
          <MaskedValue>•••</MaskedValue>
        ) : (
          <CellValue>{line.systemQty ?? '—'}</CellValue>
        ),
    },
    {
      id: 'countedQty',
      header: 'COUNTED',
      align: 'right',
      cell: (line) =>
        line.countedQty != null ? (
          <CellValue $em>{line.countedQty}</CellValue>
        ) : editable ? (
          <CountCell line={line} masked={blindActive} onCount={onCount} />
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
    {
      id: 'unitCost',
      header: 'UNIT COST',
      align: 'right',
      cell: (line) => (
        <CellValue>
          {line.unitCost != null ? `GH₵ ${line.unitCost.toLocaleString('en-US')}` : '—'}
        </CellValue>
      ),
    },
    {
      id: 'varianceQty',
      header: 'VARIANCE QTY',
      align: 'right',
      cell: (line) => {
        if (blindActive && line.varianceQty == null) return <MaskedValue>•••</MaskedValue>
        const qty = line.varianceQty
        return (
          <VarianceValue $tone={varianceToneOf(qty)}>
            {qty == null ? '—' : qty > 0 ? `+${qty}` : `${qty}`.replace('-', '−')}
          </VarianceValue>
        )
      },
    },
    {
      id: 'varianceGhs',
      header: 'VARIANCE GH₵',
      align: 'right',
      cell: (line) => {
        if (blindActive && line.varianceGhs == null) return <MaskedValue>•••</MaskedValue>
        const value = line.varianceGhs
        return (
          <>
            <VarianceValue $tone={varianceToneOf(value)}>
              {value == null ? '—' : signedGhs(value)}
            </VarianceValue>
            {thresholdsRevealed && line.overLimit === true && <OverLimitTag>Over limit</OverLimitTag>}
          </>
        )
      },
    },
    {
      id: 'note',
      header: 'NOTE',
      minWidth: '16rem',
      cell: (line) =>
        line.note ? <SheetNoteText>{line.note}</SheetNoteText> : <OtherText grey>—</OtherText>,
    },
  ]

  return (
    <>
      <BackHeader title={stockTake.name} onBack={onBack}>
        <Flex center gap='0.5rem'>
          {stockTake.blind === true && <StatusPill status='neutral'>BLIND</StatusPill>}
          <StatusPill status={meta.tone}>{meta.label}</StatusPill>
          {stockTake.status === InventoryStockTakeStatus.Scheduled && (
            <Button
              size='sm'
              variant='primary'
              disabled={actionLoading}
              onClick={() => onStart(stockTake)}
            >
              Start count
            </Button>
          )}
          {editable && (
            <Button
              size='sm'
              variant='secondary'
              disabled={actionLoading}
              onClick={() => onSubmitForApproval(stockTake)}
            >
              Submit for approval
            </Button>
          )}
        </Flex>
      </BackHeader>
      <Flex column gap='0.25rem' mb='16'>
        <DetailMetaLine>
          {`${locationLabel} · Scheduled ${scheduledLabel}${startedLabel ? ` · Started ${startedLabel}` : ''} · Counted ${countProgressLabel(counted, lines.length || null)}${deviationLimit != null ? ` · Deviation limit ${formatGhs(deviationLimit)}` : ''}`}
        </DetailMetaLine>
        {variance != null &&
          (blindActive ? (
            <Flex center gap='0.5rem'>
              <OtherText grey>Variance</OtherText>
              <MaskedValue>•••</MaskedValue>
            </Flex>
          ) : (
            <VarianceValue
              $tone={
                stockTakeVarianceTone(variance, sheetValueGhs) === 'danger'
                  ? 'danger'
                  : variance > 0
                    ? 'positive'
                    : 'normal'
              }
            >
              {`Variance ${signedGhs(variance)}`}
            </VarianceValue>
          ))}
        {blindActive && <MicroNote>{STOCK_TAKE_BLIND_CAPTION}</MicroNote>}
      </Flex>

      {stockTake.status === InventoryStockTakeStatus.AwaitingApproval && (
        <BannerWrap $tone={supervisorRequired ? 'danger' : 'warning'}>
          <Flex column gap='0.25rem'>
            <BannerText $tone={supervisorRequired ? 'danger' : 'warning'}>
              {supervisorRequired && deviationLimit != null
                ? deviationBannerText(breachedCount || 1, deviationLimit)
                : STOCK_TAKE_APPROVAL_BANNER}
            </BannerText>
            <OtherText grey>
              {`Approver — ${stockTakeApproverLabel(
                supervisorRequired
                  ? InventoryStockTakeApprovalRole.Supervisor
                  : stockTake.approvalRole,
              )}`}
            </OtherText>
          </Flex>
          <Button
            size='sm'
            variant='primary'
            disabled={actionLoading}
            onClick={() => onApprove(stockTake)}
          >
            {supervisorRequired ? 'Approve as supervisor' : 'Approve adjustments'}
          </Button>
        </BannerWrap>
      )}
      {stockTake.status === InventoryStockTakeStatus.Completed && (
        <Flex mb='16'>
          <ApproverLine>
            {`✓ Adjustments posted to stock · ${ADJUSTMENT_REASON_LABEL[3] ?? 'Count variance'} · Approved by — ${stockTakeApproverLabel(stockTake.approvalRole)}, ${approvedAtLabel ?? '—'}`}
          </ApproverLine>
        </Flex>
      )}

      <Container>
        <ContainerHeader>
          <TitleText>Count sheet</TitleText>
        </ContainerHeader>
        <ContainerContent>
          {!lines.length ? (
            <NoData text='Count sheet is generated when the count starts.' />
          ) : (
            <>
              <HonestTable
                columns={columns}
                data={lines}
                keyField='id'
                emptyMessage='Count sheet is generated when the count starts.'
              />
              {stockTake.status === InventoryStockTakeStatus.AwaitingApproval &&
                deviationLimit != null && (
                  <SheetCaption>{STOCK_TAKE_DEVIATION_CAPTION}</SheetCaption>
                )}
              <SheetCaption>{STOCK_TAKE_VARIANCE_CAPTION}</SheetCaption>
            </>
          )}
        </ContainerContent>
      </Container>
    </>
  )
}
